import { useMeasurementSystem } from 'features/inputs/contexts/measurementSystemContext';
import useSpecialtySpacesQuery from 'shared/api/specialty-spaces/useSpecialtySpacesQuery';
import useForm from 'utils/hooks/useForm';
import useUpdateEffect from 'utils/hooks/useUpdateEffect';
import { convertToCurrentSystem, convertToStandardSystem } from 'utils/units';

const getDefaultValues = (specialtySpaces, values, measurementSystem) =>
  Object.fromEntries(
    specialtySpaces.map((s) => {
      const value = values?.find((v) => v.specialtySpaceId === s.id);

      return [
        s.id.toString(),
        {
          enabled: !!value,
          area: convertToCurrentSystem(value?.area, measurementSystem)?.toString() ?? '',
          businessUnit: value?.businessUnitId?.toString() ?? s.defaultBusinessUnitId?.toString() ?? ''
        }
      ];
    })
  );

const useSpecialtySpacesController = ({ values, onSubmit }) => {
  const measurementSystem = useMeasurementSystem();

  const { data: specialtySpaces } = useSpecialtySpacesQuery();

  const { register, handleSubmit, control, setValue, reset, watch } = useForm({
    defaultValues: specialtySpaces ? getDefaultValues(specialtySpaces, values, measurementSystem) : {}
  });

  useUpdateEffect(() => {
    if (specialtySpaces && measurementSystem != null) {
      reset(getDefaultValues(specialtySpaces, values, measurementSystem));
    }
  }, [values, specialtySpaces, measurementSystem]);

  const handleFormSubmit = (values) =>
    onSubmit(
      Object.entries(values)
        .filter(([, { enabled }]) => enabled)
        .map(([id, { area, businessUnit }]) => ({
          specialtySpaceId: Number(id),
          area: area ? convertToStandardSystem(Number(area), measurementSystem) : null,
          businessUnitId: businessUnit ? Number(businessUnit) : null
        }))
    );

  return {
    register,
    onSubmit: handleSubmit(handleFormSubmit),
    control,
    setValue,
    reset,
    values: watch()
  };
};

export default useSpecialtySpacesController;
